import {
  assign,
  createStateMachine,
  type GuardFn,
  type InvokeDoneEvent,
  type State,
} from '@heidi/flow/state-machine';

import type { LinkedContextItem, TurnTerminalOutcome } from '../../../contracts';
import type { ProjectsComposerPromptDraft } from '../projects-composer-editor-draft';
import type { SubmitProjectTurnArgs } from '../submit-project-turn-args';

export type ComposerTurn = Omit<SubmitProjectTurnArgs, 'turnId'> & {
  readonly turnId: string;
  readonly draft: ProjectsComposerPromptDraft;
  readonly previewText: string;
  readonly linkedItems: readonly LinkedContextItem[];
};

export interface QueuedComposerTurn {
  id: string;
  turn: ComposerTurn;
  isEditing?: boolean;
}

export interface ProjectChatEnqueueUx {
  toast: boolean;
  clearComposer: boolean;
  remember: boolean;
}

/**
 * Activity this owner did not start. The owned-turn hold is the invoke on
 * `runningOwnedTurn`, not these flags.
 *
 * Compatibility bridges (named so they are not deleted as leftover world
 * noise):
 * - `turnInFlight` — foreign/remote turns and remount hosts that have not
 *   migrated onto this owner (`awaitingExternalTurn`).
 * - `isSubmitting` — the window after a named terminal lands while POST 1
 *   is still in flight; draining then would hit overlap.
 * - `isStartingSeededTurn` — a remount host has a pending seed that has not
 *   yet been adopted by the destination owner.
 */
export type TurnCommandWorld = {
  turnInFlight: boolean;
  isSubmitting: boolean;
  isStartingSeededTurn: boolean;
};

/** Composer-side work that is not the turn POST itself. */
export type TurnCommandEffect =
  | { kind: 'enqueue'; turn: ComposerTurn; ux: ProjectChatEnqueueUx }
  | { kind: 'interrupt' };

export type TurnCommandContext = {
  queue: QueuedComposerTurn[];
  nextId: number;
  /** Last accepted enqueue text — refuses the same message twice in a row. */
  lastFingerprint: string | null;
  effect: TurnCommandEffect | null;
  effectSeq: number;
  /** The command the invoked starter will POST. */
  activeCommand: ComposerTurn | null;
  ownsComposerDraft: boolean;
  /** An owned dispatch ended without landing: queued turns park until a
   *  deliberate send. */
  dispatchUnsent: boolean;
};

type WorldEventType =
  | 'TURN_STARTED'
  | 'OPENING_TURN_HELD'
  | 'QUEUE_READY'
  | 'TURN_SETTLED';

type WorldEvent = { type: WorldEventType; world: TurnCommandWorld };
type SendEvent = {
  type: 'SEND';
  turn: ComposerTurn;
  ux: ProjectChatEnqueueUx;
  world: TurnCommandWorld;
};
type SendNowEvent = {
  type: 'SEND_NOW';
  id: string | null;
  turn: ComposerTurn;
  world: TurnCommandWorld;
};
type TakeOwnedNowEvent = {
  type: 'TAKE_OWNED_NOW';
  id: string | null;
  turn: ComposerTurn;
  world: TurnCommandWorld;
};
type RemoveQueuedEvent = { type: 'REMOVE_QUEUED'; id: string };
/** Full rebuilt turn from the composition side — never a raw body string. */
type UpdateQueuedEvent = { type: 'UPDATE_QUEUED'; id: string; turn: ComposerTurn };
type ClearQueueEvent = { type: 'CLEAR_QUEUE' };
type SetQueuedEditingEvent = {
  type: 'SET_QUEUED_EDITING';
  id: string;
  isEditing: boolean;
};
type DraftChangedEvent = { type: 'DRAFT_CHANGED'; fingerprint: string };

export type TurnCommandEvent =
  | WorldEvent
  | SendEvent
  | SendNowEvent
  | TakeOwnedNowEvent
  | RemoveQueuedEvent
  | UpdateQueuedEvent
  | ClearQueueEvent
  | SetQueuedEditingEvent
  | DraftChangedEvent;

export type TurnCommandStateValue =
  | 'idle'
  | 'runningOwnedTurn'
  | 'readyToDispatch'
  | 'awaitingExternalTurn'
  | 'holdingOpeningTurn';

export type TurnCommandState = State<TurnCommandContext, TurnCommandStateValue>;

export const initialTurnCommandContext: TurnCommandContext = {
  queue: [],
  nextId: 0,
  lastFingerprint: null,
  effect: null,
  effectSeq: 0,
  activeCommand: null,
  ownsComposerDraft: false,
  dispatchUnsent: false,
};

const externalTurnIsBusy = (world: TurnCommandWorld): boolean =>
  world.turnInFlight || world.isSubmitting || world.isStartingSeededTurn;

/**
 * Names the phase the chat is in. Owned-turn occupancy is the invoke, so
 * these events never describe "we dispatched and are waiting for a boundary".
 */
export const worldEventFor = (
  world: TurnCommandWorld,
  context: Pick<TurnCommandContext, 'queue' | 'dispatchUnsent'>
): WorldEventType => {
  if (externalTurnIsBusy(world)) return 'TURN_STARTED';
  if (context.queue.length === 0) return 'TURN_SETTLED';
  if (context.dispatchUnsent) return 'OPENING_TURN_HELD';
  return 'QUEUE_READY';
};

/** A queued or in-flight owned command keeps the composer busy. */
export const isTurnCommandBusy = (state: TurnCommandState): boolean =>
  state.value !== 'idle';

const fingerprintOf = (turn: ComposerTurn): string =>
  (turn.displayText ?? turn.text).trim();

const acceptsFingerprint = (context: TurnCommandContext, fingerprint: string): boolean =>
  fingerprint !== '' && fingerprint !== context.lastFingerprint;

const withEffect = (
  context: TurnCommandContext,
  effect: TurnCommandEffect
): Pick<TurnCommandContext, 'effect' | 'effectSeq'> => ({
  effect,
  effectSeq: context.effectSeq + 1,
});

const mintQueuedTurn = (
  context: TurnCommandContext,
  id: string | null,
  turn: ComposerTurn
): { queued: QueuedComposerTurn; nextId: number } =>
  id === null
    ? { queued: { id: `queued-${context.nextId}`, turn }, nextId: context.nextId + 1 }
    : { queued: { id, turn }, nextId: context.nextId };

const takeOwnedSend = assign<TurnCommandContext, SendEvent>((_context, event) => ({
  activeCommand: event.turn,
  ownsComposerDraft: event.ux.clearComposer,
  lastFingerprint: fingerprintOf(event.turn),
  dispatchUnsent: false,
}));

const takeOwnedSendNow = assign<TurnCommandContext, SendNowEvent | TakeOwnedNowEvent>(
  (context, event) => ({
    queue: context.queue.filter((queued) => queued.id !== event.id),
    activeCommand: event.turn,
    ownsComposerDraft: false,
    dispatchUnsent: false,
  })
);

const enqueueCommand = assign<TurnCommandContext, SendEvent>((context, event) => {
  const { queued, nextId } = mintQueuedTurn(context, null, event.turn);
  return {
    queue: [...context.queue, queued],
    nextId,
    lastFingerprint: fingerprintOf(event.turn),
    ...withEffect(context, { kind: 'enqueue', turn: event.turn, ux: event.ux }),
  };
});

// A seeded opening turn is not cancellable yet, so it is promoted over, not stopped.
const promoteQueuedTurn = assign<TurnCommandContext, SendNowEvent | TakeOwnedNowEvent>(
  (context, event) => {
    const { queued, nextId } = mintQueuedTurn(context, event.id, event.turn);
    const queue = [queued, ...context.queue.filter((item) => item.id !== queued.id)];
    if (event.world.isStartingSeededTurn) return { queue, nextId };
    return { queue, nextId, ...withEffect(context, { kind: 'interrupt' }) };
  }
);

const drainQueueHead = assign<TurnCommandContext, WorldEvent>((context) => {
  const [head, ...rest] = context.queue;
  if (head === undefined) return {};
  return {
    queue: rest,
    activeCommand: head.turn,
    ownsComposerDraft: false,
    dispatchUnsent: false,
  };
});

const releaseOwnedTurn = assign<TurnCommandContext>(() => ({
  activeCommand: null,
  ownsComposerDraft: false,
}));

/** The invoked dispatch reports its own failure to land; no world flag does. */
const recordDispatchOutcome = assign<
  TurnCommandContext,
  InvokeDoneEvent<TurnTerminalOutcome>
>((_context, event) => ({
  dispatchUnsent:
    event.data?.kind === 'abandoned' && event.data.reason === 'submit_rejected',
}));

const armDispatchUnsent = assign<TurnCommandContext>(() => ({ dispatchUnsent: true }));

const removeQueuedTurn = assign<TurnCommandContext, RemoveQueuedEvent>(
  (context, event) => {
    const removed = context.queue.find((queued) => queued.id === event.id);
    const resetsFingerprint =
      removed !== undefined && fingerprintOf(removed.turn) === context.lastFingerprint;
    return {
      queue: context.queue.filter((queued) => queued.id !== event.id),
      lastFingerprint: resetsFingerprint ? null : context.lastFingerprint,
    };
  }
);

const updateQueuedTurn = assign<TurnCommandContext, UpdateQueuedEvent>(
  (context, event) => {
    const previous = context.queue.find((queued) => queued.id === event.id);
    const resetsFingerprint =
      previous !== undefined && fingerprintOf(previous.turn) === context.lastFingerprint;
    return {
      queue: context.queue.map((queued) =>
        queued.id === event.id
          ? { ...queued, turn: event.turn, isEditing: false }
          : queued
      ),
      lastFingerprint: resetsFingerprint ? null : context.lastFingerprint,
    };
  }
);

const clearQueue = assign<TurnCommandContext>(() => ({
  queue: [],
  lastFingerprint: null,
}));

const setQueuedEditing = assign<TurnCommandContext, SetQueuedEditingEvent>(
  (context, event) => {
    if (!context.queue.some((queued) => queued.id === event.id)) return {};
    return {
      queue: context.queue.map((queued) =>
        queued.id === event.id ? { ...queued, isEditing: event.isEditing } : queued
      ),
    };
  }
);

const resetEnqueueGate = assign<TurnCommandContext>(() => ({ lastFingerprint: null }));

const settlesEnqueueGate = (
  context: TurnCommandContext,
  world: TurnCommandWorld
): boolean =>
  !world.turnInFlight &&
  !world.isSubmitting &&
  context.activeCommand === null &&
  context.queue.length === 0 &&
  context.lastFingerprint !== null;

const applyWorld = assign<TurnCommandContext, WorldEvent>((context, event) =>
  settlesEnqueueGate(context, event.world) ? { lastFingerprint: null } : {}
);

export const turnCommandGuards: Record<
  string,
  GuardFn<TurnCommandContext, TurnCommandEvent>
> = {
  settlesToIdle: (context) =>
    context.queue.length === 0 && context.activeCommand === null,
  enqueueAccepted: (context, event) =>
    event.type === 'SEND' && acceptsFingerprint(context, fingerprintOf(event.turn)),
  canDrainQueue: (context, event) =>
    'world' in event &&
    context.activeCommand === null &&
    context.queue.length > 0 &&
    !context.dispatchUnsent &&
    !externalTurnIsBusy(event.world) &&
    !context.queue[0]?.isEditing,
  settlesEnqueueGate: (context, event) =>
    'world' in event && settlesEnqueueGate(context, event.world),
  clearsEnqueueGate: (context, event) =>
    event.type === 'DRAFT_CHANGED' &&
    context.lastFingerprint !== null &&
    event.fingerprint.trim() !== context.lastFingerprint,
};

const RUN_OWNED_SEND = {
  target: 'runningOwnedTurn',
  actions: takeOwnedSend,
} as const;
const RUN_OWNED_SEND_NOW = {
  target: 'runningOwnedTurn',
  actions: takeOwnedSendNow,
} as const;
const DRAIN_QUEUE = {
  target: 'runningOwnedTurn',
  guard: 'canDrainQueue',
  actions: drainQueueHead,
} as const;
const SETTLE_TO_IDLE = {
  target: 'idle',
  guard: 'settlesToIdle',
  actions: resetEnqueueGate,
} as const;
const STAY = { guard: 'settlesEnqueueGate', actions: resetEnqueueGate } as const;
const enterWorld = (target: TurnCommandStateValue) =>
  ({ target, actions: applyWorld }) as const;

const QUEUE_EDITS = {
  REMOVE_QUEUED: { actions: removeQueuedTurn },
  UPDATE_QUEUED: { actions: updateQueuedTurn },
  CLEAR_QUEUE: { actions: clearQueue },
  SET_QUEUED_EDITING: { actions: setQueuedEditing },
  DRAFT_CHANGED: { guard: 'clearsEnqueueGate', actions: resetEnqueueGate },
} as const;

/**
 * The composer's turn-command owner: what is waiting, when it drains, and
 * whether a fresh send goes out now or joins the tail.
 *
 * `idle` — nothing running and nothing waiting, so a send starts an owned turn.
 * `runningOwnedTurn` — invoke holds until that exact operation's terminal.
 * `readyToDispatch` — non-invoked seam after terminal; required so the next
 * invoke restarts (`@heidi/flow` does not restart invoke on a same-state target).
 * `awaitingExternalTurn` — a foreign/remote turn, or the isSubmitting overlap
 * window, until the world is quiet enough to drain.
 * `holdingOpeningTurn` — the chat owes an opening turn: queued turns park and a
 * deliberate send IS the (possibly revised) opening command.
 */
export const turnCommandMachine = createStateMachine({
  id: 'turnCommand',
  initial: 'idle',
  context: initialTurnCommandContext,
  states: {
    idle: {
      on: {
        SEND: RUN_OWNED_SEND,
        SEND_NOW: RUN_OWNED_SEND_NOW,
        TAKE_OWNED_NOW: RUN_OWNED_SEND_NOW,
        QUEUE_READY: DRAIN_QUEUE,
        TURN_STARTED: enterWorld('awaitingExternalTurn'),
        OPENING_TURN_HELD: enterWorld('holdingOpeningTurn'),
        TURN_SETTLED: STAY,
        ...QUEUE_EDITS,
      },
    },
    runningOwnedTurn: {
      invoke: {
        src: 'startTurnAndWaitForTerminal',
        onDone: {
          target: 'readyToDispatch',
          actions: [releaseOwnedTurn, recordDispatchOutcome],
        },
        onError: {
          target: 'readyToDispatch',
          actions: [releaseOwnedTurn, armDispatchUnsent],
        },
      },
      on: {
        SEND: { guard: 'enqueueAccepted', actions: enqueueCommand },
        SEND_NOW: { actions: promoteQueuedTurn },
        TAKE_OWNED_NOW: { actions: promoteQueuedTurn },
        // World events are internal stays: sync must not abort the invoke.
        QUEUE_READY: STAY,
        TURN_STARTED: STAY,
        OPENING_TURN_HELD: STAY,
        TURN_SETTLED: STAY,
        ...QUEUE_EDITS,
      },
    },
    readyToDispatch: {
      on: {
        SEND: RUN_OWNED_SEND,
        SEND_NOW: RUN_OWNED_SEND_NOW,
        TAKE_OWNED_NOW: RUN_OWNED_SEND_NOW,
        QUEUE_READY: DRAIN_QUEUE,
        TURN_STARTED: enterWorld('awaitingExternalTurn'),
        OPENING_TURN_HELD: enterWorld('holdingOpeningTurn'),
        TURN_SETTLED: SETTLE_TO_IDLE,
        ...QUEUE_EDITS,
      },
    },
    awaitingExternalTurn: {
      on: {
        SEND: { guard: 'enqueueAccepted', actions: enqueueCommand },
        SEND_NOW: { actions: promoteQueuedTurn },
        TAKE_OWNED_NOW: RUN_OWNED_SEND_NOW,
        QUEUE_READY: DRAIN_QUEUE,
        TURN_STARTED: STAY,
        OPENING_TURN_HELD: enterWorld('holdingOpeningTurn'),
        TURN_SETTLED: SETTLE_TO_IDLE,
        ...QUEUE_EDITS,
      },
    },
    holdingOpeningTurn: {
      on: {
        SEND: RUN_OWNED_SEND,
        SEND_NOW: RUN_OWNED_SEND_NOW,
        TAKE_OWNED_NOW: RUN_OWNED_SEND_NOW,
        QUEUE_READY: DRAIN_QUEUE,
        TURN_STARTED: enterWorld('awaitingExternalTurn'),
        OPENING_TURN_HELD: STAY,
        TURN_SETTLED: SETTLE_TO_IDLE,
        ...QUEUE_EDITS,
      },
    },
  },
});
